// Yeni bir sürüm yayınlandığında açık sekmelerin eski kodla kalmaması için
// sunucudaki index.html'deki main bundle adını, şu an yüklü olanla karşılaştırır.
// Yeni sürüm bulunduğunda:
//  - "Yeni sürüm var" uyarısı gösterilir,
//  - kullanıcı site içinde başka bir sayfaya geçtiğinde sayfa kendiliğinden yenilenir
//    (zaten o sayfadan çıktığı için yazdığı bir şey kaybolmaz),
//  - sekme arka plandayken, form/sınav/ödeme/admin sayfasında değilse ve sayfada bir şey
//    yazılmamışsa sayfa kendiliğinden yenilenir.

const CHECK_INTERVAL_MS = 60 * 1000;
const MAIN_BUNDLE_REGEX = /\/static\/js\/main\.[a-z0-9]+\.chunk\.js/;
// Aynı sürüm için en fazla bir kez otomatik yenileriz; yenileme sonrası hâlâ eski sürüm
// geliyorsa (ör. yayın sırasında kısa bir tutarsızlık) döngüye girmeden sadece uyarı kalır.
const RELOADED_FOR_KEY = "version_check_reloaded_for";

// Bu sayfalarda arka planda otomatik yenileme yapılmaz; yalnızca uyarı gösterilir
// ve sayfa değiştirildiğinde yenilenir.
const NO_BACKGROUND_RELOAD_PATHS = [
  /^\/giris/,
  /^\/kayit/,
  /^\/coachsignup/,
  /^\/sifre-/,
  /^\/satinal/,
  /^\/payment_result/,
  /^\/exam\//,
  /^\/oyun2021\//,
  /^\/admin/,
];

function getLoadedBundle() {
  const script = Array.from(document.scripts).find((s) =>
    MAIN_BUNDLE_REGEX.test(s.src)
  );
  return script ? script.src.match(MAIN_BUNDLE_REGEX)[0] : null;
}

async function fetchIndex(method) {
  return fetch(`/index.html?v=${Date.now()}`, { method, cache: "no-store" });
}

async function getLatestBundle() {
  const response = await fetchIndex("GET");
  if (!response.ok) return { bundle: null, etag: null };
  const html = await response.text();
  const match = html.match(MAIN_BUNDLE_REGEX);
  return {
    bundle: match ? match[0] : null,
    etag: response.headers.get("etag"),
  };
}

function safeSessionGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSessionSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {}
}

// Sayfanın en üstünde, tam genişlikte, sitenin turuncusuyla dikkat çeken bir bant.
function showUpdateBanner() {
  const banner = document.createElement("div");
  banner.setAttribute("role", "alert");
  Object.assign(banner.style, {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    zIndex: "2147483647",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px 20px",
    padding: "14px 16px",
    background: "rgb(251, 128, 70)",
    color: "#fff",
    fontSize: "17px",
    fontWeight: "700",
    textAlign: "center",
    boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
  });

  const text = document.createElement("span");
  text.textContent =
    "🔔 Sitenin yeni bir sürümü yayınlandı. Güncel halini görmek için sayfayı yenileyin.";

  const button = document.createElement("button");
  button.type = "button";
  button.textContent = "Şimdi Yenile";
  Object.assign(button.style, {
    flexShrink: "0",
    padding: "8px 22px",
    border: "2px solid #fff",
    borderRadius: "6px",
    background: "#fff",
    color: "rgb(214, 90, 32)",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
  });
  button.addEventListener("click", () => window.location.reload());

  banner.appendChild(text);
  banner.appendChild(button);
  document.body.appendChild(banner);

  // Yukarıdan kayarak gelsin, sonra dikkat çekmek için hafifçe titresin.
  if (banner.animate) {
    banner.animate(
      [{ transform: "translateY(-100%)" }, { transform: "translateY(0)" }],
      { duration: 400, easing: "ease-out" }
    );
    button.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.08)" },
        { transform: "scale(1)" },
      ],
      { duration: 1200, delay: 400, iterations: Infinity }
    );
  }
}

// history: connected-react-router geçmişi (sayfa geçişlerini yakalamak için)
// persistor: redux-persist; yenilemeden önce oturum bilgisini diske yazdırırız ki kaybolmasın.
export function startVersionCheck({ history, persistor } = {}) {
  if (process.env.NODE_ENV !== "production") return;

  const loadedBundle = getLoadedBundle();
  if (!loadedBundle) return;

  let latestBundle = null;
  let knownEtag = null;
  let isReloading = false;
  // Sayfaya bir şey yazıldıysa arka planda yenilemiyoruz. Sayfa değişince sıfırlanır.
  let hasTypedOnThisPage = false;
  document.addEventListener("input", () => (hasTypedOnThisPage = true), true);

  const reload = (url) => {
    if (isReloading) return;
    if (safeSessionGet(RELOADED_FOR_KEY) === latestBundle) return; // döngü koruması
    isReloading = true;
    safeSessionSet(RELOADED_FOR_KEY, latestBundle);
    const go = () =>
      url ? window.location.assign(url) : window.location.reload();
    const flush = persistor ? persistor.flush() : Promise.resolve();
    flush.then(go, go);
  };

  const canReloadInBackground = () =>
    document.visibilityState === "hidden" &&
    !hasTypedOnThisPage &&
    !NO_BACKGROUND_RELOAD_PATHS.some((re) => re.test(window.location.pathname));

  const onUpdateFound = () => {
    showUpdateBanner();
    if (history)
      history.listen((location) =>
        reload(location.pathname + location.search + location.hash)
      );
    if (canReloadInBackground()) reload();
  };

  const check = async () => {
    if (latestBundle || isReloading) return;
    // Sekme görünmüyorsa sunucuya istek atmıyoruz; geri dönüldüğünde kontrol edilir.
    if (document.visibilityState === "hidden") return;
    try {
      // Önce sadece başlığı (ETag) soruyoruz; değişmemişse sayfanın tamamını indirmeye gerek yok.
      if (knownEtag) {
        const head = await fetchIndex("HEAD");
        if (head.ok && head.headers.get("etag") === knownEtag) return;
      }
      const latest = await getLatestBundle();
      knownEtag = latest.etag;
      if (latest.bundle && latest.bundle !== loadedBundle) {
        latestBundle = latest.bundle;
        onUpdateFound();
      }
    } catch (e) {
      // Ağ hatası: bir sonraki kontrolde tekrar denenir.
    }
  };

  if (history) history.listen(() => (hasTypedOnThisPage = false));

  check();
  setInterval(check, CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
    else if (latestBundle && canReloadInBackground()) reload();
  });
  window.addEventListener("focus", check);
}
